// Music player — plays the theme MP3 in a loop
// Uses HTMLAudioElement with localStorage persistence for volume/mute state.

const STORAGE_VOLUME = 'theloop-volume';
const STORAGE_MUTED = 'theloop-muted';
const DEFAULT_VOLUME = 0.3;

export interface MusicPlayer {
  play: () => void;
  setVolume: (v: number) => void;
  getVolume: () => number;
  setMuted: (m: boolean) => void;
  isMuted: () => boolean;
  isPlaying: () => boolean;
  dispose: () => void;
}

export function createMusicPlayer(): MusicPlayer | null {
  // Respect prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return null;
  }

  const audio = new Audio();
  audio.loop = true;
  audio.preload = 'none';

  // Restore saved preferences
  const savedVol = localStorage.getItem(STORAGE_VOLUME);
  const vol = savedVol !== null ? parseFloat(savedVol) : DEFAULT_VOLUME;
  audio.volume = Math.max(0, Math.min(1, vol));

  // Always start muted — user must click to enable audio
  audio.muted = true;

  let playing = false;
  let loaded = false;

  function ensureLoaded(): void {
    if (loaded) return;
    loaded = true;
    audio.src = '/static/medias/theloopio.mp3';
    audio.load();
  }

  // Preload once all critical assets (CSS, JS, images, fonts) are done
  if (document.readyState === 'complete') {
    ensureLoaded();
  } else {
    window.addEventListener('load', ensureLoaded, { once: true });
  }

  function play(): void {
    if (playing) return;
    ensureLoaded();
    audio.play().then(() => { playing = true; }).catch(() => {});
  }

  function setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    audio.volume = clamped;
    localStorage.setItem(STORAGE_VOLUME, String(clamped));
  }

  function setMuted(m: boolean): void {
    audio.muted = m;
    localStorage.setItem(STORAGE_MUTED, m ? '1' : '0');
  }

  function dispose(): void {
    audio.pause();
    audio.src = '';
    playing = false;
  }

  return {
    play,
    setVolume,
    getVolume: () => audio.volume,
    setMuted,
    isMuted: () => audio.muted,
    isPlaying: () => playing,
    dispose,
  };
}
